'use client';

import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { useEffect, useState, useRef } from 'react';

import { getQueryRetryDelay, isRetryableQueryError } from '@/lib/query-resilience';
import { shouldPersistQuery } from '@/lib/query-cache-persistence';

export const QUERY_CACHE_KEY = 'menuraq-query-cache';

// Bumped with the dehydrate filter below: existing clients are carrying a ~2.2MB
// cache on disk, and only a new buster evicts it.
// v4: caches written by v3 can hold pending queries with a dead `promise`.
const QUERY_CACHE_BUSTER = 'v4-success-only';

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 60,
        // `true` (not `'always'`): a remount refetches only when the cached data
        // is stale. `'always'` made every page visit re-ask the server even for
        // data minutes old — the root cause of the "rechecks on every visit"
        // redirect lag the access-snapshot work removes.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
          return failureCount < 3 && isRetryableQueryError(error);
        },
        retryDelay: getQueryRetryDelay,
        networkMode: 'offlineFirst',
      },
      mutations: {
        networkMode: 'offlineFirst',
      },
    },
  }));

  // Pause/resume queries based on network status
  const prevOnlineRef = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      prevOnlineRef.current = true;
      queryClient.resumePausedMutations();
      queryClient.invalidateQueries();
    };

    const handleOffline = () => {
      prevOnlineRef.current = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queryClient]);

  // The cache used to be restored inside a useEffect, i.e. *after* the first
  // paint , so every hard reload rendered one frame with an empty cache and any
  // gate reading that cache (billing, most visibly) showed a loading state for
  // data we already had on disk. PersistQueryClientProvider restores before it
  // renders children, so a reload resumes from cache instead of re-asking.
  const [persistOptions] = useState(() => ({
    persister: createSyncStoragePersister({
      storage: typeof window === 'undefined' ? undefined : window.localStorage,
      key: QUERY_CACHE_KEY,
    }),
    maxAge: 1000 * 60 * 60,
    buster: QUERY_CACHE_BUSTER,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
    },
  }));

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      {children}
    </PersistQueryClientProvider>
  );
}
